/**
 * `financialControls` namespace (ALC-4520). Shared copy lives here; each domain's section copy and
 * each control's copy live in their own files and are wired with ONE line per locale below, so
 * parallel tickets and domains never collide.
 */
import { apGl05En, apGl05PtBr } from '@/lib/i18n/financial-controls/ap-gl05';
import { apSectionEn, apSectionPtBr } from '@/lib/i18n/financial-controls/ap-section';
import { ap03En, ap03PtBr } from '@/lib/i18n/financial-controls/ap03';
import { ap04En, ap04PtBr } from '@/lib/i18n/financial-controls/ap04';
import { ap05En, ap05PtBr } from '@/lib/i18n/financial-controls/ap05';
import { ap06En, ap06PtBr } from '@/lib/i18n/financial-controls/ap06';
import { ap07En, ap07PtBr } from '@/lib/i18n/financial-controls/ap07';
import { ap08En, ap08PtBr } from '@/lib/i18n/financial-controls/ap08';
import { ap09En, ap09PtBr } from '@/lib/i18n/financial-controls/ap09';
import { ap10En, ap10PtBr } from '@/lib/i18n/financial-controls/ap10';
import { ap11En, ap11PtBr } from '@/lib/i18n/financial-controls/ap11';
import { arSectionEn, arSectionPtBr } from '@/lib/i18n/financial-controls/ar-section';
import { ar01En, ar01PtBr } from '@/lib/i18n/financial-controls/ar01';
import { ar02En, ar02PtBr } from '@/lib/i18n/financial-controls/ar02';
import { ar03En, ar03PtBr } from '@/lib/i18n/financial-controls/ar03';
import { ar04En, ar04PtBr } from '@/lib/i18n/financial-controls/ar04';
import { ar05En, ar05PtBr } from '@/lib/i18n/financial-controls/ar05';
import { ar06En, ar06PtBr } from '@/lib/i18n/financial-controls/ar06';
import { ar07En, ar07PtBr } from '@/lib/i18n/financial-controls/ar07';
import { ar08En, ar08PtBr } from '@/lib/i18n/financial-controls/ar08';
import { atSectionEn, atSectionPtBr } from '@/lib/i18n/financial-controls/at-section';
import { at01En, at01PtBr } from '@/lib/i18n/financial-controls/at01';
import { at02En, at02PtBr } from '@/lib/i18n/financial-controls/at02';
import { at03En, at03PtBr } from '@/lib/i18n/financial-controls/at03';
import { at04En, at04PtBr } from '@/lib/i18n/financial-controls/at04';
import { at05En, at05PtBr } from '@/lib/i18n/financial-controls/at05';
import { at06En, at06PtBr } from '@/lib/i18n/financial-controls/at06';
import { at07En, at07PtBr } from '@/lib/i18n/financial-controls/at07';
import {
  assessmentErrorsEn,
  assessmentErrorsPtBr,
  historyEn,
  historyPtBr,
} from '@/lib/i18n/financial-controls/history';
import {
  treasuryDetailEn,
  treasuryDetailPtBr,
  trSectionEn,
  trSectionPtBr,
} from '@/lib/i18n/financial-controls/tr-section';
import { tr01En, tr01PtBr } from '@/lib/i18n/financial-controls/tr01';
import { tr02En, tr02PtBr } from '@/lib/i18n/financial-controls/tr02';
import { tr03En, tr03PtBr } from '@/lib/i18n/financial-controls/tr03';
import { tr04En, tr04PtBr } from '@/lib/i18n/financial-controls/tr04';
import { tr05En, tr05PtBr } from '@/lib/i18n/financial-controls/tr05';
import { tr06En, tr06PtBr } from '@/lib/i18n/financial-controls/tr06';

export const financialControlsPtBr = {
  sections: {
    AP: apSectionPtBr,
    AR: arSectionPtBr,
    TR: trSectionPtBr,
    AT: atSectionPtBr,
  },
  treasuryDetail: treasuryDetailPtBr,
  section: {
    loading: 'Avaliando controles',
    loadError: 'Não foi possível avaliar os controles.',
    retry: 'Tentar novamente',
    empty: 'Nenhum controle disponível para este período.',
  },
  status: {
    comingSoon: 'Em breve',
    failed: 'Erro na avaliação',
    SATISFIED: 'Em dia',
    UNSATISFIED: 'Com pendências',
    UNDETERMINED: 'Indeterminado',
  },
  metrics: {
    count_one: '{{count}} pendência',
    count_other: '{{count}} pendências',
    noTotal: 'Sem valor',
    countLabel: 'Pendências',
    amountLabel: 'Valor',
    observedLabel: 'Avaliação',
    observedAt: 'Avaliado em {{date}}',
    restricted: 'Itens confidenciais da folha não aparecem para você.',
  },
  currencyConfig: {
    entity: 'Entidade',
    book: 'Livro',
    configuredCurrency: 'Moeda configurada',
    bookCurrencyCode: 'Moeda de apresentação do livro',
    cause: 'Motivo',
    noValue: 'Sem valor',
    causes: {
      MISSING: 'Vínculo entre entidade e livro ausente ou sem moeda',
      ENTITY_CURRENCY_MISSING: 'Moeda funcional da entidade não configurada',
      EMPTY: 'Moeda configurada em branco',
      UNSUPPORTED: 'Código de moeda não reconhecido',
    },
  },
  errors: {
    FINANCIAL_CONTROL_SCOPE_NOT_FOUND:
      'Período contábil não encontrado para esta entidade e livro.',
    FINANCIAL_CONTROL_EVALUATION_FAILED:
      'A avaliação deste controle falhou. Nenhum resultado foi considerado.',
    FINANCIAL_CONTROL_DIAGNOSTIC_FAILED:
      'O diagnóstico parcial falhou. O controle continua em breve.',
    AT05_ENTITY_BOOK_MISSING:
      'O AT05 não encontrou o vínculo entre a entidade e o livro. Cadastre o livro da entidade com o seu calendário e avalie de novo.',
    AT05_FISCAL_YEAR_CALENDAR_MISSING:
      'O AT05 não encontrou o calendário do livro da entidade. Escolha um calendário existente no livro da entidade e avalie de novo.',
    ...assessmentErrorsPtBr,
  },
  diagnostic: {
    label: 'Diagnóstico parcial',
    caption: 'Cobre apenas fontes conhecidas; não confirma que o período está completo.',
  },
  details: {
    showAction: 'Ver detalhes',
    actionLabel: 'Ver detalhes analisados e pendentes do controle {{controlId}}',
    coverageLabel: 'Cobertura analisada',
    applicabilityLabel: 'Aplicabilidade',
    ruleLabel: 'Regra executada',
    notAvailable: 'Não disponível',
    coverage: {
      COMPLETE: 'Completa para as fontes implementadas',
      PARTIAL: 'Parcial; existem fontes fora da avaliação',
      MISSING: 'Ausente',
      UNKNOWN: 'Não determinada',
    },
    applicability: {
      APPLICABLE: 'Aplicável ao período',
      NOT_APPLICABLE: 'Não aplicável ao período',
      UNDETERMINED: 'Não determinada',
    },
  },
  history: historyPtBr,
  controls: {
    AP03: ap03PtBr,
    AP04: ap04PtBr,
    AP05: ap05PtBr,
    AP06: ap06PtBr,
    AP07: ap07PtBr,
    AP08: ap08PtBr,
    AP09: ap09PtBr,
    AP10: ap10PtBr,
    AP11: ap11PtBr,
    AP_GL05: apGl05PtBr,
    AR01: ar01PtBr,
    AR02: ar02PtBr,
    AR03: ar03PtBr,
    AR04: ar04PtBr,
    AR05: ar05PtBr,
    AR06: ar06PtBr,
    AR07: ar07PtBr,
    AR08: ar08PtBr,
    TR01: tr01PtBr,
    TR02: tr02PtBr,
    TR03: tr03PtBr,
    TR04: tr04PtBr,
    TR05: tr05PtBr,
    TR06: tr06PtBr,
    AT01: at01PtBr,
    AT02: at02PtBr,
    AT03: at03PtBr,
    AT04: at04PtBr,
    AT05: at05PtBr,
    AT06: at06PtBr,
    AT07: at07PtBr,
  },
};

export const financialControlsEn: typeof financialControlsPtBr = {
  sections: {
    AP: apSectionEn,
    AR: arSectionEn,
    TR: trSectionEn,
    AT: atSectionEn,
  },
  treasuryDetail: treasuryDetailEn,
  section: {
    loading: 'Evaluating controls',
    loadError: 'Could not evaluate the controls.',
    retry: 'Try again',
    empty: 'No controls available for this period.',
  },
  status: {
    comingSoon: 'Coming soon',
    failed: 'Evaluation error',
    SATISFIED: 'Clear',
    UNSATISFIED: 'Needs attention',
    UNDETERMINED: 'Undetermined',
  },
  metrics: {
    count_one: '{{count}} open item',
    count_other: '{{count}} open items',
    noTotal: 'No amount',
    countLabel: 'Open items',
    amountLabel: 'Amount',
    observedLabel: 'Evaluation',
    observedAt: 'Evaluated on {{date}}',
    restricted: 'Confidential payroll items are hidden from you.',
  },
  currencyConfig: {
    entity: 'Entity',
    book: 'Book',
    configuredCurrency: 'Configured currency',
    bookCurrencyCode: 'Book presentation currency',
    cause: 'Reason',
    noValue: 'No value',
    causes: {
      MISSING: 'Entity and book link missing or has no currency',
      ENTITY_CURRENCY_MISSING: 'Entity functional currency is not configured',
      EMPTY: 'Configured currency is blank',
      UNSUPPORTED: 'Currency code not recognized',
    },
  },
  errors: {
    FINANCIAL_CONTROL_SCOPE_NOT_FOUND: 'Accounting period not found for this entity and book.',
    FINANCIAL_CONTROL_EVALUATION_FAILED: 'This control failed to evaluate. No result was counted.',
    FINANCIAL_CONTROL_DIAGNOSTIC_FAILED:
      'The partial diagnostic failed. The control is still coming soon.',
    AT05_ENTITY_BOOK_MISSING:
      'AT05 found no link between the entity and the book. Set up the entity book with its calendar and evaluate again.',
    AT05_FISCAL_YEAR_CALENDAR_MISSING:
      "AT05 could not find the calendar of the entity's book. Pick an existing calendar on the entity book and evaluate again.",
    ...assessmentErrorsEn,
  },
  diagnostic: {
    label: 'Partial diagnostic',
    caption: 'Covers known sources only; does not confirm the period is complete.',
  },
  details: {
    showAction: 'View details',
    actionLabel: 'View analyzed scope and open items for control {{controlId}}',
    coverageLabel: 'Analyzed coverage',
    applicabilityLabel: 'Applicability',
    ruleLabel: 'Executed rule',
    notAvailable: 'Not available',
    coverage: {
      COMPLETE: 'Complete for implemented sources',
      PARTIAL: 'Partial; some sources are outside the evaluation',
      MISSING: 'Missing',
      UNKNOWN: 'Undetermined',
    },
    applicability: {
      APPLICABLE: 'Applicable to the period',
      NOT_APPLICABLE: 'Not applicable to the period',
      UNDETERMINED: 'Undetermined',
    },
  },
  history: historyEn,
  controls: {
    AP03: ap03En,
    AP04: ap04En,
    AP05: ap05En,
    AP06: ap06En,
    AP07: ap07En,
    AP08: ap08En,
    AP09: ap09En,
    AP10: ap10En,
    AP11: ap11En,
    AP_GL05: apGl05En,
    AR01: ar01En,
    AR02: ar02En,
    AR03: ar03En,
    AR04: ar04En,
    AR05: ar05En,
    AR06: ar06En,
    AR07: ar07En,
    AR08: ar08En,
    TR01: tr01En,
    TR02: tr02En,
    TR03: tr03En,
    TR04: tr04En,
    TR05: tr05En,
    TR06: tr06En,
    AT01: at01En,
    AT02: at02En,
    AT03: at03En,
    AT04: at04En,
    AT05: at05En,
    AT06: at06En,
    AT07: at07En,
  },
};
